import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { LockKey, CheckCircle, ArrowRight } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [status, setStatus] = useState("verifying"); // verifying | valid | invalid | done
  const [errorMsg, setErrorMsg] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setErrorMsg("This reset link is missing a token.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/reset-password/verify", { token });
        setName(data.name || "");
        setStatus("valid");
      } catch (err) {
        setStatus("invalid");
        setErrorMsg(formatApiError(err.response?.data?.detail) || "Reset link is invalid.");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setStatus("done");
      toast.success("Password updated. You can sign in now.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <img
          src="https://customer-assets-v7afamib.emergentagent.net/job_hr-employee-portal-1/artifacts/16j2gtd7_GREENRGY%20NEW%20FINAL%20LOGO.webp"
          alt="Greenrgy"
          className="h-10 w-auto mb-8"
        />
        <div className="label-caps mb-3">Password recovery</div>
        <h1 className="font-display font-black text-4xl tracking-tight leading-none">Set a new password</h1>

        {status === "verifying" && (
          <p className="mt-6 text-sm text-[#6B7280]" data-testid="reset-verifying">Checking your reset link…</p>
        )}

        {status === "invalid" && (
          <div className="mt-8 border border-[#E5E7EB] p-6 bg-[#FEF2F2]" data-testid="reset-invalid">
            <div className="font-semibold text-[#B91C1C]">Link cannot be used</div>
            <p className="mt-2 text-sm text-[#7F1D1D]">{errorMsg}</p>
            <Link to="/login" className="brand-btn mt-6 inline-flex items-center gap-2 py-2.5 px-4 text-sm">
              Back to sign in <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {status === "valid" && (
          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="reset-form">
            {name && <p className="text-sm text-[#6B7280]">Setting a new password for <strong>{name}</strong>.</p>}
            <div>
              <label className="label-caps block mb-2">New password</label>
              <div className="relative">
                <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                <input
                  data-testid="reset-new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  required
                  className="w-full border border-[#E5E7EB] pl-10 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#106109]/30 focus:border-[#106109]"
                />
              </div>
            </div>
            <div>
              <label className="label-caps block mb-2">Confirm new password</label>
              <div className="relative">
                <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                <input
                  data-testid="reset-confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={6}
                  required
                  className="w-full border border-[#E5E7EB] pl-10 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#106109]/30 focus:border-[#106109]"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="reset-submit"
              className="brand-btn w-full py-3.5 text-sm font-semibold tracking-wide disabled:opacity-60"
            >
              {saving ? "Updating…" : "Update password"}
            </button>
          </form>
        )}

        {status === "done" && (
          <div className="mt-8 border border-[#E5E7EB] p-6 bg-[#ECFDF5]" data-testid="reset-done">
            <div className="flex items-center gap-2 text-[#065F46] font-semibold">
              <CheckCircle size={20} weight="fill" /> Password updated
            </div>
            <p className="mt-2 text-sm text-[#065F46]">You can now sign in with your new password.</p>
            <button
              onClick={() => navigate("/login")}
              data-testid="reset-goto-login"
              className="brand-btn mt-6 inline-flex items-center gap-2 py-2.5 px-4 text-sm"
            >
              Go to sign in <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
